import { Box, Card, Heading, Paragraph, Text, Tooltip } from '@zillow/constellation-10';
import { CREDIT_SCORE_LACK_OF_INFORMATION, getNumberFromString } from '@zg-rentals/applications-utils';
import { UserType } from '../../models/report-models';
import { type CreditReportType } from '@zg-rentals/ts-capps-client';

export function CreditReportSummary({
  creditReport,
  userType,
}: {
  creditReport: CreditReportType;
  userType?: UserType;
}) {
  const creditScore = getNumberFromString(creditReport.creditScore);
  const metricValueOnError = creditScore === CREDIT_SCORE_LACK_OF_INFORMATION ? 'N/A' : null;

  const onTimePaymentsPercent = metricValueOnError ?? creditReport.onTimePaymentsPercent;
  const estimatedMonthlyPayments = metricValueOnError ?? creditReport.estimatedMonthlyPayments;
  const creditUsed = metricValueOnError ?? creditReport.creditUsed;
  const totalDebt = metricValueOnError ?? creditReport.totalDebt;
  return (
    <>
      <Heading level={3} textStyle="heading-sm" css={{ marginTop: 'loose', marginBottom: 'loose' }}>
        Financial summary
      </Heading>

      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'default', marginBottom: 'default' }}>
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 4', md: 'span 2', xl: 'span 1' } }}>
          <Card elevated={false}>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Paragraph>On-time payments</Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'default', marginBottom: 'default' }}>
                {onTimePaymentsPercent ? onTimePaymentsPercent : 'None found'}
              </Text>
            </Box>
          </Card>
        </Box>
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 4', md: 'span 2', xl: 'span 1' } }}>
          <Card elevated={false}>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Paragraph>
                Est. mo. payments{' '}
                <Tooltip content="This is the total amount of minimum payments due from all revolving accounts (such as credit cards) and installment accounts (such as active student loans, auto loans or mortgage payments)." />
              </Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'default', marginBottom: 'default' }}>
                {estimatedMonthlyPayments ? estimatedMonthlyPayments : 'None found'}
              </Text>
            </Box>
          </Card>
        </Box>
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 4', md: 'span 2', xl: 'span 1' } }}>
          <Card elevated={false}>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Paragraph>
                Credit used{' '}
                <Tooltip
                  content={`This is the percentage of available credit being used. For example, if there is one credit card with a $5,000 limit and the balance is $1,000, 20% of available credit is used.${
                    userType == UserType.Renter ? ' A high percentage can negatively affect your credit score.' : ''
                  }`}
                />
              </Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'default', marginBottom: 'default' }}>
                {creditUsed ? creditUsed : 'None found'}
              </Text>
            </Box>
          </Card>
        </Box>
        <Box css={{ display: 'grid', gridColumnEnd: { base: 'span 4', md: 'span 2', xl: 'span 1' } }}>
          <Card elevated={false}>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Paragraph>Total debt</Paragraph>
              <Text textStyle="heading-md" css={{ marginTop: 'default', marginBottom: 'default' }}>
                {totalDebt ? totalDebt : 'None found'}
              </Text>
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
}
